import { ChatStreamMode } from '../constants/chatStreamMode';

export const INSIGHT_TAB_MODES = {
  explain: ChatStreamMode.VerseExplain,
  simple: ChatStreamMode.Simple,
  application: ChatStreamMode.Application,
};

/** Как ChatStreamModeHelper.IsFollowUp (без Application — у него свой путь со snapshot). */
export function isChatFollowUpMode(mode) {
  return (
    mode === ChatStreamMode.Simple ||
    mode === ChatStreamMode.KeyIdea ||
    mode === ChatStreamMode.Deep ||
    mode === ChatStreamMode.Example ||
    mode === ChatStreamMode.Faq
  );
}

/** Ввод из поля чата, не чип Insight / follow-up. */
export function isFreeformUserMessage(m) {
  return m?.role === 'user' && !m.asFollowupChip && !m.isFollowUp;
}

export function messageBelongsToInsightTab(m, tabId) {
  if (!m || !tabId) return false;
  if (m.insightTab === tabId) return true;
  const mode = INSIGHT_TAB_MODES[tabId];
  return mode != null && m.modeTag === mode;
}

/** Повтор того же чипа до первого своего вопроса на этой вкладке — не добавляем в ленту. */
export function shouldSkipRepeatVerseChip(messages, mode) {
  const tabId = Object.keys(INSIGHT_TAB_MODES).find((k) => INSIGHT_TAB_MODES[k] === mode);
  const scoped = tabId
    ? (messages || []).filter((m) => messageBelongsToInsightTab(m, tabId))
    : messages || [];
  if (!Array.isArray(scoped) || scoped.some(isFreeformUserMessage)) return false;
  return scoped.some(
    (m) =>
      m?.role === 'assistant' &&
      m.modeTag === mode &&
      (m.streaming || String(m.text || '').trim().length > 0)
  );
}

function dedupeTurns(items) {
  const seen = new Set();
  const result = [];
  for (const raw of items || []) {
    const text = String(raw || '').trim();
    if (!text) continue;
    if (seen.has(text)) continue;
    seen.add(text);
    result.push(text);
  }
  return result;
}

/**
 * Для follow-up: только последний ответ ассистента и вопрос, к которому он относится.
 * Иначе «Разобрать глубже» может уйти в старый вопрос из длинной цепочки.
 */
export function trimSnapshotForFollowUp({ initialQuestion, userTurns = [], assistantTurns = [] }) {
  const assistants = dedupeTurns(assistantTurns);
  if (assistants.length === 0) {
    return { initialQuestion, userTurns, assistantTurns };
  }
  const users = dedupeTurns(userTurns);
  const lastIndex = assistants.length - 1;
  const focusQuestion =
    lastIndex === 0 ? initialQuestion : users[lastIndex - 1] || initialQuestion;
  return {
    initialQuestion: focusQuestion,
    userTurns: [],
    assistantTurns: [assistants[lastIndex]],
  };
}

export function buildCleanSnapshot(snapshot, messages, { forFollowUp = false } = {}) {
  if (!snapshot?.initialQuestion) return null;
  const freeformUsers = dedupeTurns(
    (messages || []).filter(isFreeformUserMessage).map((m) => String(m.text || '').trim())
  );
  const initialQuestion = freeformUsers[0] || snapshot.initialQuestion;
  const userTurns = freeformUsers.slice(1);
  const assistantTurns = dedupeTurns(
    (messages || [])
      .filter((m) => m?.role === 'assistant')
      .filter((m) => !m.streaming)
      .map((m) => String(m.text || '').trim())
  );

  if (assistantTurns.length === 0) {
    const fallback = dedupeTurns(snapshot.assistantTurns || []);
    const base = {
      initialQuestion,
      userTurns,
      assistantTurns: fallback.slice(0, 1),
    };
    return forFollowUp ? trimSnapshotForFollowUp(base) : base;
  }

  const base = {
    initialQuestion,
    userTurns,
    assistantTurns,
  };
  return forFollowUp ? trimSnapshotForFollowUp(base) : base;
}

/** Последние N реплик в контекст уточняющего вопроса (без текущего ввода). */
export const INSIGHT_QUESTION_CONTEXT_MAX_MESSAGES = 5;
/** Лимит символов на реплику пользователя в контексте. */
export const INSIGHT_QUESTION_CONTEXT_MAX_CHARS = 520;
/** Лимит символов на ответ ассистента (разбор вкладки) в контексте. */
export const INSIGHT_QUESTION_CONTEXT_MAX_ASSISTANT_CHARS = 2000;

function stripInsightHtml(text) {
  return String(text || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncateInsightText(text, max = INSIGHT_QUESTION_CONTEXT_MAX_CHARS) {
  const normalized = stripInsightHtml(text);
  if (!normalized) return '';
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1).trim()}…`;
}

function trimInsightDialogTurns(assistantTurns, userTurns) {
  if (assistantTurns.length === 0) {
    return { assistantTurns: [], userTurns: userTurns.slice(-INSIGHT_QUESTION_CONTEXT_MAX_MESSAGES) };
  }

  const head = [assistantTurns[0]];
  let restAssistants = assistantTurns.slice(1);
  let users = [...userTurns];
  const maxTail = INSIGHT_QUESTION_CONTEXT_MAX_MESSAGES;

  while (restAssistants.length + users.length > maxTail) {
    if (users.length > 0 && restAssistants.length > 0) {
      users.shift();
      restAssistants.shift();
    } else if (users.length > 0) {
      users.shift();
    } else if (restAssistants.length > 0) {
      restAssistants.shift();
    } else {
      break;
    }
  }

  return {
    assistantTurns: [...head, ...restAssistants],
    userTurns: users,
  };
}

function isInsightContextUserMessage(m) {
  return m?.role === 'user' && !m.asFollowupChip && !m.isFollowUp;
}

/**
 * Snapshot для уточняющего вопроса: стих с бэкенда,
 * контекст — разбор вкладки и прошлые уточнения (отдельные assistant/user turns).
 */
export function buildInsightQuestionSnapshot(
  snapshot,
  messages,
  tabId,
  defaultInitialQuestion = ''
) {
  const initialQuestion =
    String(snapshot?.initialQuestion || defaultInitialQuestion || '').trim() ||
    defaultInitialQuestion ||
    '';

  const scoped = (messages || []).filter(
    (m) => m && !m.streaming && messageBelongsToInsightTab(m, tabId)
  );

  const assistantTurns = [];
  const userTurns = [];

  for (const m of scoped) {
    if (m.role === 'assistant') {
      const maxChars =
        m.modeTag === ChatStreamMode.Question
          ? INSIGHT_QUESTION_CONTEXT_MAX_CHARS
          : INSIGHT_QUESTION_CONTEXT_MAX_ASSISTANT_CHARS;
      const text = truncateInsightText(m.text, maxChars);
      if (text) assistantTurns.push(text);
    } else if (isInsightContextUserMessage(m)) {
      const text = truncateInsightText(m.text);
      if (text) userTurns.push(text);
    }
  }

  const trimmed = trimInsightDialogTurns(assistantTurns, userTurns);

  return {
    initialQuestion,
    userTurns: trimmed.userTurns,
    assistantTurns: trimmed.assistantTurns,
  };
}

/**
 * Ответ ассистента для вкладки Insight (разбор / просто / применение).
 */
export function getInsightSurfaceAssistant(messages, insightActiveMode = null) {
  const targetMode =
    insightActiveMode === 'application'
      ? ChatStreamMode.Application
      : insightActiveMode === 'explain'
        ? ChatStreamMode.VerseExplain
        : insightActiveMode === 'simple'
          ? ChatStreamMode.Simple
          : insightActiveMode === 'keyIdea'
            ? ChatStreamMode.KeyIdea
            : null;

  if (targetMode != null) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m?.role === 'assistant' && m.modeTag === targetMode) return m;
    }
  }

  const firstFreeIdx = messages.findIndex(isFreeformUserMessage);
  if (firstFreeIdx === -1) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return messages[i];
    }
    return null;
  }
  let lastUser = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      lastUser = messages[i];
      break;
    }
  }
  if (lastUser?.asFollowupChip) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return messages[i];
    }
    return null;
  }
  let last = null;
  for (let i = 0; i < firstFreeIdx; i += 1) {
    if (messages[i]?.role === 'assistant') last = messages[i];
  }
  return last;
}

/** Уточняющие вопросы и ответы только для указанной вкладки. */
export function getQuestionThreadMessages(messages, tabId) {
  if (!tabId) return [];
  return (messages || []).filter((m) => {
    if (!messageBelongsToInsightTab(m, tabId)) return false;
    if (isFreeformUserMessage(m)) return true;
    return m.role === 'assistant' && m.modeTag === ChatStreamMode.Question;
  });
}
